import React from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Clock, Star, Zap, Heart, Sword, Crown, Globe, Skull, Coffee, Lightbulb } from "lucide-react";
// FIX: Replaced useLocation from "wouter" with "react-router-dom" to resolve module import errors and ensure compatibility with the project's routing setup.
import { useNavigate } from "react-router-dom";
import { BookType } from "@shared/story-constants";

interface Template {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  bookType: BookType;
  targetWordCount: number;
  icon: JSX.Element;
  characteristics: {
    genre: string;
    tone: string;
    style: string;
    themes: string[];
    setting: string;
    protagonist: string;
    conflict: string;
    pacing: string;
  };
  prompt: string;
}

const templates: Template[] = [
  {
    id: "1",
    title: "Epic Fantasy Adventure",
    description: "Grand tale of magic, quests, and mythical realms with complex world-building",
    category: "Fantasy",
    difficulty: "Advanced",
    bookType: "NOVEL",
    targetWordCount: 95000,
    icon: <Sword className="h-5 w-5" />,
    characteristics: {
      genre: "High Fantasy",
      tone: "Epic and adventurous",
      style: "Rich descriptive prose with detailed world-building",
      themes: ["Good vs. evil", "Coming of age", "Sacrifice for greater good", "Power and responsibility"],
      setting: "Fictional magical world with distinct cultures and landscapes",
      protagonist: "Unlikely hero discovering hidden powers or destiny",
      conflict: "Dark lord or ancient evil threatening the world",
      pacing: "Escalating tension with action-packed sequences"
    },
    prompt: "Write a story about a young farm worker who discovers they have magical abilities and must embark on a quest to save their village from an ancient evil."
  },
  {
    id: "2",
    title: "Sci-Fi Space Opera",
    description: "Futuristic mystery with cutting-edge technology and moral dilemmas",
    category: "Science Fiction",
    difficulty: "Intermediate",
    bookType: "NOVEL",
    targetWordCount: 80000,
    icon: <Zap className="h-5 w-5" />,
    characteristics: {
      genre: "Science Fiction Thriller",
      tone: "Tense and thought-provoking",
      style: "Fast-paced with technical details and philosophical questions",
      themes: ["Technology vs. humanity", "Ethics of progress", "Identity in digital age", "Surveillance and freedom"],
      setting: "Near-future dystopian or space-based environment",
      protagonist: "Scientist, detective, or rebel questioning the system",
      conflict: "Corporate conspiracy or AI uprising threatening society",
      pacing: "Rapid with cliffhangers and plot twists"
    },
    prompt: "Create a story set in a distant future where humanity has colonized multiple star systems, and a discovery on a remote planet threatens to change everything."
  },
  {
    id: "3",
    title: "Cozy Mystery",
    description: "Charming amateur sleuth solving crimes in a close-knit community",
    category: "Mystery",
    difficulty: "Beginner",
    bookType: "NOVEL",
    targetWordCount: 70000,
    icon: <Crown className="h-5 w-5" />,
    characteristics: {
      genre: "Cozy Mystery",
      tone: "Light-hearted yet intriguing",
      style: "Gentle pacing with character focus and community dynamics",
      themes: ["Justice and truth", "Community bonds", "Hidden secrets", "Appearance vs. reality"],
      setting: "Small town, village, or specialized community (bookstore, café, etc.)",
      protagonist: "Amateur detective with local knowledge and sharp observation",
      conflict: "Murder or theft disrupting peaceful community life",
      pacing: "Methodical investigation with red herrings and revelations"
    },
    prompt: "Write a mystery about a detective investigating a series of seemingly unconnected crimes that reveal a deeper conspiracy."
  },
  {
    id: "4",
    title: "Contemporary Romance",
    description: "Heartwarming love story with emotional depth and character growth",
    category: "Romance",
    difficulty: "Beginner",
    bookType: "NOVEL",
    targetWordCount: 65000,
    icon: <Heart className="h-5 w-5" />,
    characteristics: {
      genre: "Contemporary Romance",
      tone: "Warm and emotional",
      style: "Character-driven with intimate dialogue",
      themes: ["Love conquers all", "Self-discovery", "Trust and vulnerability", "Second chances"],
      setting: "Modern urban or small town setting",
      protagonist: "Relatable character seeking love and personal growth",
      conflict: "Misunderstandings, past trauma, or external obstacles to love",
      pacing: "Steady build-up with emotional crescendos"
    },
    prompt: "Tell the story of two people from different worlds who meet by chance and find themselves drawn together despite all odds."
  },
  {
    id: "5",
    title: "Historical Drama",
    description: "Immersive story set during a pivotal historical period",
    category: "Historical Fiction",
    difficulty: "Advanced",
    bookType: "NOVEL",
    targetWordCount: 85000,
    icon: <Globe className="h-5 w-5" />,
    characteristics: {
      genre: "Historical Fiction",
      tone: "Authentic and immersive",
      style: "Period-appropriate language with rich historical detail",
      themes: ["Survival and resilience", "Social change", "Cultural identity", "Moral choices in difficult times"],
      setting: "Historically accurate time and place (World War era, Victorian England, etc.)",
      protagonist: "Character navigating historical events and social constraints",
      conflict: "Personal struggles against backdrop of historical upheaval",
      pacing: "Deliberate with focus on character development and historical atmosphere"
    },
    prompt: "Set your story during a significant historical period and show how ordinary people were affected by extraordinary events."
  },
  {
    id: "6",
    title: "Psychological Horror",
    description: "Spine-chilling tale of supernatural or psychological terror",
    category: "Horror",
    difficulty: "Intermediate",
    bookType: "NOVEL",
    targetWordCount: 75000,
    icon: <Skull className="h-5 w-5" />,
    characteristics: {
      genre: "Psychological Horror",
      tone: "Dark and suspenseful",
      style: "Atmospheric writing with building tension and vivid imagery",
      themes: ["Fear of the unknown", "Madness vs. reality", "Isolation and paranoia", "Consequences of past actions"],
      setting: "Isolated location (haunted house, remote town, abandoned facility)",
      protagonist: "Character with dark past or supernatural sensitivity",
      conflict: "Malevolent force or psychological breakdown threatening sanity",
      pacing: "Slow burn building to intense climactic moments"
    },
    prompt: "Create a horror story that builds psychological tension rather than relying on gore, focusing on what isn't seen or said."
  },
  {
    id: "7",
    title: "Coming-of-Age Novella",
    description: "Personal growth journey during formative years",
    category: "Literary Fiction",
    difficulty: "Beginner",
    bookType: "NOVELLA",
    targetWordCount: 25000,
    icon: <Lightbulb className="h-5 w-5" />,
    characteristics: {
      genre: "Coming-of-Age Literary Fiction",
      tone: "Reflective and poignant",
      style: "Introspective narrative with lyrical prose",
      themes: ["Self-discovery", "Loss of innocence", "Family dynamics", "Finding one's place in the world"],
      setting: "Familiar environment (hometown, school, family setting)",
      protagonist: "Young person facing life-changing events or decisions",
      conflict: "Internal struggle with identity and external pressures",
      pacing: "Contemplative with moments of revelation and growth"
    },
    prompt: "Write about a teenager experiencing a pivotal moment that changes their understanding of themselves and the world around them."
  },
  {
    id: "8",
    title: "Coffee Shop Chronicles",
    description: "Interconnected short stories centered around a neighborhood café",
    category: "Literary",
    difficulty: "Beginner",
    bookType: "SHORT_STORY",
    targetWordCount: 8000,
    icon: <Coffee className="h-5 w-5" />,
    characteristics: {
      genre: "Contemporary Literary Fiction",
      tone: "Warm and observational",
      style: "Slice-of-life vignettes with interconnected characters",
      themes: ["Human connection", "Daily miracles", "Community", "Life's small moments"],
      setting: "Cozy neighborhood coffee shop and surrounding area",
      protagonist: "Various customers and staff sharing brief but meaningful moments",
      conflict: "Subtle personal struggles and small triumphs of everyday life",
      pacing: "Gentle and episodic with satisfying emotional resolutions"
    },
    prompt: "Create a collection of interconnected stories about the regulars who frequent a local coffee shop, showing how their lives intersect in meaningful ways."
  }
];

const getDifficultyColor = (difficulty: string) => {
  switch (difficulty) {
    case "Beginner": return "bg-green-100 text-green-800";
    case "Intermediate": return "bg-yellow-100 text-yellow-800";
    case "Advanced": return "bg-red-100 text-red-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

export default function TemplatesPage() {
// FIX: Replaced useLocation from "wouter" with "react-router-dom" to resolve module import errors and ensure compatibility with the project's routing setup.
  const navigate = useNavigate();

  const handleUseTemplate = (template: Template) => {
    // Navigate to story-idea page with template characteristics
    const params = new URLSearchParams({
      template: template.id,
      bookType: template.bookType,
      targetWordCount: template.targetWordCount.toString(),
      genre: template.characteristics.genre,
      tone: template.characteristics.tone,
      style: template.characteristics.style,
      themes: template.characteristics.themes.join(', '),
      setting: template.characteristics.setting,
      protagonist: template.characteristics.protagonist,
      conflict: template.characteristics.conflict,
      pacing: template.characteristics.pacing,
      prompt: template.prompt
    });
    
    navigate(`/story-idea?${params.toString()}`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Story Templates</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Choose from our curated collection of story templates to jumpstart your writing journey.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {template.icon}
                    </div>
                    <div>
                      <Badge variant="outline" className="text-xs">{template.category}</Badge>
                      <Badge className={`text-xs ml-2 ${getDifficultyColor(template.difficulty)}`}>
                        {template.difficulty}
                      </Badge>
                    </div>
                  </div>
                </div>
                <CardTitle className="text-lg">{template.title}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Book Type:</span>
                    <span className="font-medium">{template.bookType.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Target Words:</span>
                    <span className="font-medium">{template.targetWordCount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Genre:</span>
                    <span className="font-medium text-xs">{template.characteristics.genre}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium">Key Themes:</span>
                  <div className="flex flex-wrap gap-1">
                    {template.characteristics.themes.slice(0, 3).map((theme, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {theme}
                      </Badge>
                    ))}
                    {template.characteristics.themes.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{template.characteristics.themes.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>

                <Button 
                  onClick={() => handleUseTemplate(template)}
                  className="w-full h-12 sm:h-auto"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Use Template
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}